import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { toastFailure } from "@/utils/toastFailure";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { useConfirmPreassignedCampaignsMutation } from "@/pages/Private/redux/campaignRouting/campaignRouting.api";

import { ReachOutBulkTarget, reachOutBulkScope } from "../ReachOut/reachOutTarget";
import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import { RoutingTranslate } from "./utils/campaignRoutingText";
import { ConfirmPlan, confirmPlanOf } from "./utils/confirmEligibility";
import { confirmResultMessage, skippedBeforeMessage } from "./utils/confirmResultMessage";

/** The confirm plan of a bulk reach-out selection: its loaded rows, against its list's campaigns. */
export const confirmPlanOfTarget = (target: ReachOutBulkTarget): ConfirmPlan =>
	confirmPlanOf({
		selectType: target.selectType,
		selectedItems: target.selectedItems,
		selectedRows: target.selectedRows,
		campaignConfig: target.list?.campaignConfig as Record<string, unknown> | null | undefined,
	});

interface ConfirmPreassignedModalProps {
	target: ReachOutBulkTarget;
	/** Shows the server's answer after the dialog has closed (the table's snackbar). */
	onResult: (message: string) => void;
	onClose: () => void;
}

/**
 * "Confirm preassigned campaigns" (PREC-4219): says before the commit how many leads it will
 * confirm and how many it skips, and why (`confirmPlanOf`, the server's rule on the loaded rows);
 * for a select-all, which the server resolves, it says what it does without promising a number.
 * Then it enrolls each into the campaign it already holds. No campaign is picked here, so none
 * is forced onto the selection. The request carries the selection as every bulk action sends it
 * (the page's ids, or a select-all the server re-derives from the table's view). The result message
 * is built from the server's answer; a refusal shows in the dialog, or in a toast once it is closed.
 */
export const ConfirmPreassignedModal: FunctionComponent<ConfirmPreassignedModalProps> = ({
	target,
	onResult,
	onClose,
}) => {
	const { t } = useTranslation();
	const [confirmPreassigned] = useConfirmPreassignedCampaignsMutation();
	const { run, isRunning } = useSingleFlight();
	const [failed, setFailed] = useState(false);
	// Read once: the dialog describes the selection it was opened on.
	const [plan] = useState(() => confirmPlanOfTarget(target));
	const skippedLine =
		plan.kind === "counted" ? skippedBeforeMessage(plan.skipped, t as RoutingTranslate) : null;
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;
		};
	}, []);

	const confirm = () =>
		run(async () => {
			setFailed(false);

			try {
				const result = await confirmPreassigned({
					listId: target.listId,
					assign: target.selectedItems.map((listPersonId) => ({ listPersonId })),
					...reachOutBulkScope(target),
				}).unwrap();

				onResult(confirmResultMessage(result, t as RoutingTranslate));
			} catch (error) {
				if (mountedRef.current) {
					setFailed(true);
				} else {
					toastFailure(error, t(`${ROUTING_I18N}.confirm.failed`));
				}

				return;
			}

			if (mountedRef.current) {
				onClose();
			}
		});

	return (
		<Modal
			takeFocus
			handleClose={onClose}
			handleSave={confirm}
			isLoading={isRunning}
			isOpened={true}
			submitButtonText={t(`${ROUTING_I18N}.confirm.submit`)}
			title={t(`${ROUTING_I18N}.confirm.title`)}
		>
			<div className="flex flex-col gap-3 text-sm text-text" data-testid="confirm-preassigned">
				<p data-testid="confirm-preassigned-count">
					{plan.kind === "counted"
						? t(`${ROUTING_I18N}.confirm.body`, { count: plan.eligible })
						: t(`${ROUTING_I18N}.confirm.bodyServerResolved`)}
				</p>
				{skippedLine && <p data-testid="confirm-preassigned-skipped">{skippedLine}</p>}
				{plan.kind === "counted" && (
					<p className="text-text-meta">{t(`${ROUTING_I18N}.confirm.serverChecks`)}</p>
				)}
				<p className="text-text-meta">{t(`${ROUTING_I18N}.confirm.noSingleCampaign`)}</p>
				{failed && (
					<div
						className="rounded border border-status-error bg-status-error-light p-2 text-status-error"
						role="alert"
					>
						{t(`${ROUTING_I18N}.confirm.failed`)}
					</div>
				)}
			</div>
		</Modal>
	);
};

/**
 * The bulk "Confirm preassigned campaigns" dialog for a table: `open` takes the selection as the
 * reach-out menu sees it; the dialog is mounted only while open (the shared Modal is keepMounted).
 */
export const useConfirmPreassigned = (showMessage: (message: string) => void) => {
	const [target, setTarget] = useState<ReachOutBulkTarget | null>(null);
	const open = useCallback((selection: ReachOutBulkTarget) => setTarget(selection), []);
	const close = useCallback(() => setTarget(null), []);

	const modal = target ? (
		<ConfirmPreassignedModal target={target} onClose={close} onResult={showMessage} />
	) : null;

	return { open, modal };
};
